import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Edit3, X } from "lucide-react";
import { getSinglePenStyle } from "./singlePenStyles";
import { AUTO_BLEEDING_TRIGGER_CODES } from "./chartState";

const SURFACE_ORDER = ["M", "O", "D", "B", "L"];

const GROUPS = [
  { id: "caries", label: "Caries", collapsedLabel: "caries", codes: ["caries"] },
  { id: "fracture", label: "Fracture", collapsedLabel: "fracture", codes: ["fracture", "cracked_tooth"] },
  { id: "defective_restoration", label: "Defective Restoration", collapsedLabel: "defect", codes: ["defective_restoration"] },
  { id: "endo", label: "Endodontic Findings", collapsedLabel: "endo", codes: ["pulpitis", "necrotic_pulp", "periapical_lesion", "failed_rct"] },
  { id: "wear", label: "Wear Findings", collapsedLabel: "wear", codes: ["wear", "abrasion", "abfraction", "erosion"] },
  { id: "perio", label: "Periodontal Findings", collapsedLabel: "perio", codes: ["mobility", "perio_pocket", "recession", "furcation", "bleeding_on_probing", "calculus"] },
  { id: "inflammation", label: "Inflammation", collapsedLabel: "inflammation", codes: ["inflammation"] },
  { id: "flags", label: "Tooth Flags", collapsedLabel: "flag", codes: [] },
  { id: "other", label: "Other / Custom Pens", collapsedLabel: "other", codes: [] },
];

const GROUP_BY_CODE = GROUPS.reduce((acc, group) => {
  for (const code of group.codes) acc[code] = group.id;
  return acc;
}, {});

const SEVERITY_CATEGORY_CODES = new Set([
  "caries",
  "defective_restoration",
  "fracture",
  "wear",
  "mobility",
  "perio_pocket",
  "calculus",
  "recession",
  "pulpitis",
  "inflammation",
  "periapical_lesion",
  "furcation",
  "abrasion",
  "abfraction",
  "erosion",
  "cracked_tooth",
]);

const SEVERITY_READBACK = {
  inc: "Incipient",
  incipient: "Incipient",
  mod: "Moderate",
  moderate: "Moderate",
  deep: "Deep",
  pulp: "Pulp involvement",
  enam: "Into enamel",
  enamel: "Into enamel",
  crk: "Cracked",
  crack: "Cracked",
  cracked: "Cracked",
  dent: "Into dentin",
  dentin: "Into dentin",
  g1: "Grade 1",
  g2: "Grade 2",
  g3: "Grade 3",
  "4-5": "4-5mm",
  "6-7": "6-7mm",
  "8+": "8mm+",
  mild: "Mild",
  severe: "Severe",
  reversible: "Reversible",
  irreversible: "Irreversible",
  supra: "Supragingival",
  sub: "Subgingival",
  open: "Open margin",
  lost: "Filling lost",
};

export default function ChartedFindingsSummaryPanel({
  state,
  findings = [],
  completedWorkByTooth = {},
  readOnly = false,
  defaultCollapsed = false,
  onEditFinding,
  onRemoveFinding,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);
  const entries = useMemo(
    () => buildChartedFindingEntries(state, findings, completedWorkByTooth),
    [state, findings, completedWorkByTooth],
  );
  const grouped = useMemo(() => groupEntries(entries), [entries]);
  const collapsedSummary = useMemo(() => buildCollapsedSummary(grouped, entries.length), [grouped, entries.length]);

  useEffect(() => {
    setCollapsed(defaultCollapsed);
    setPendingRemoveId(null);
  }, [defaultCollapsed]);

  return (
    <section
      className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70"
      data-testid="charted-findings-summary"
      data-tutorial-id="charting-findings-summary"
    >
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="charted-findings-summary-toggle"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              Charted Findings · {entries.length}
            </h3>
            {entries.length > 0 ? (
              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 border border-slate-200">
                {entries.length}
              </span>
            ) : null}
          </div>
          {collapsed ? (
            <p className="mt-0.5 truncate text-xs text-slate-500">{collapsedSummary}</p>
          ) : null}
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
      </button>

      {!collapsed ? (
        <div className="border-t border-slate-200 px-4 py-4">
          {entries.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No findings charted yet. Tap a tooth surface to start.
            </div>
          ) : (
            <div className="space-y-5">
              {GROUPS.map((group) => {
                const items = grouped[group.id] || [];
                if (items.length === 0) return null;
                return (
                  <div key={group.id} data-testid={`charted-findings-group-${group.id}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                        {group.label} ({items.length})
                      </p>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <ul className="space-y-1.5">
                      {items.map((entry) => (
                        <SummaryEntry
                          key={entry.id}
                          entry={entry}
                          readOnly={readOnly}
                          pendingRemoveId={pendingRemoveId}
                          onEditFinding={onEditFinding}
                          onAskRemove={() => setPendingRemoveId(entry.id)}
                          onCancelRemove={() => setPendingRemoveId(null)}
                          onConfirmRemove={() => {
                            onRemoveFinding?.(entry);
                            setPendingRemoveId(null);
                          }}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function SummaryEntry({
  entry,
  readOnly,
  pendingRemoveId,
  onEditFinding,
  onAskRemove,
  onCancelRemove,
  onConfirmRemove,
}) {
  const confirming = pendingRemoveId === entry.id;
  return (
    <li className="flex items-center gap-3 rounded-lg bg-white px-3 py-2 text-sm text-slate-800 border border-slate-100">
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: entry.color || "#94a3b8" }}
      />
      <span className="min-w-0 flex-1 leading-5">{entry.readback}</span>
      {!readOnly ? (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {confirming ? (
            <>
              <span className="px-1 text-xs font-semibold text-rose-700">Remove?</span>
              <button
                type="button"
                onClick={onConfirmRemove}
                className="rounded-md bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                data-testid={`charted-finding-remove-confirm-${entry.id}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={onCancelRemove}
                className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"
              >
                No
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onEditFinding?.(entry)}
                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                data-testid={`charted-finding-edit-${entry.id}`}
              >
                <Edit3 className="h-3 w-3" />
                Edit
              </button>
              <button
                type="button"
                onClick={onAskRemove}
                aria-label={`Remove ${entry.readback}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                data-testid={`charted-finding-remove-${entry.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

export function buildChartedFindingEntries(state, findings = [], completedWorkByTooth = {}) {
  const byCode = new Map(findings.map((finding) => [finding.code, finding]));
  const teeth = state?.teeth || {};
  return Object.entries(teeth)
    .flatMap(([toothNumber, tooth]) => {
      return (tooth?.findings || [])
        .filter((finding) => finding.finding_code && finding.finding_code !== "note")
        .map((finding) => filterResolvedFindingSurfaces(toothNumber, finding, byCode, completedWorkByTooth))
        .filter(Boolean)
        .map((finding) => buildEntry(toothNumber, finding, byCode, tooth?.findings || []));
    })
    .sort((a, b) => Number(a.toothNumber) - Number(b.toothNumber) || a.readback.localeCompare(b.readback));
}

function filterResolvedFindingSurfaces(toothNumber, finding, byCode, completedWorkByTooth) {
  const code = finding.finding_code;
  const catalog = byCode.get(code) || {};
  const categoryCode = catalog.category_code || catalog.code || code;
  if (categoryCode !== "caries" && code !== "caries" && !String(code || "").startsWith("caries_")) {
    return finding;
  }

  const surfaces = normalizeSurfaces(finding.surfaces);
  if (surfaces.length === 0) return finding;

  const resolved = resolvedCariesSurfacesForTooth(toothNumber, completedWorkByTooth);
  if (resolved.size === 0) return finding;

  const remaining = surfaces.filter((surface) => !resolved.has(surface));
  if (remaining.length === surfaces.length) return finding;
  if (remaining.length === 0) return null;
  return { ...finding, surfaces: remaining };
}

function resolvedCariesSurfacesForTooth(toothNumber, completedWorkByTooth = {}) {
  const out = new Set();
  const workItems = completedWorkByTooth[String(toothNumber)] || [];
  for (const item of workItems) {
    if (item?.resolves_caries_findings !== true) continue;
    const surfaces = normalizeSurfaces(item.surface_codes);
    if (surfaces.length === 0) {
      for (const surface of SURFACE_ORDER) out.add(surface);
    } else {
      for (const surface of surfaces) out.add(surface);
    }
  }
  return out;
}

function buildEntry(toothNumber, finding, byCode, toothFindings = []) {
  const code = finding.finding_code;
  const catalog = byCode.get(code) || {};
  const categoryCode = catalog.category_code || catalog.code || code;
  const isSinglePen = !!getSinglePenStyle(code) || !!getSinglePenStyle(categoryCode);
  const groupId = getGroupId(categoryCode, code, isSinglePen);
  const surfaces = isSinglePen || finding.auto_flagged_by ? [] : normalizeSurfaces(finding.surfaces);
  const severityLabel = extractSeverityLabel(catalog, categoryCode, code, isSinglePen);
  const bleedingAutoFlagged = AUTO_BLEEDING_TRIGGER_CODES.has(code)
    && toothFindings.some((item) => item.finding_code === "bleeding_on_probing" && item.auto_flagged_by === "inflammation");
  const readback = formatFindingReadback({
    toothNumber,
    surfaces,
    severityLabel,
    isSinglePen,
    singlePenLabel: catalog.display_name || catalog.name || getSinglePenStyle(code)?.label || code,
    suffix: bleedingAutoFlagged ? "(bleeding auto-flagged)" : null,
  });

  return {
    id: `${toothNumber}-${code}`,
    toothNumber,
    code,
    categoryCode,
    groupId,
    color: catalog.color_hex || getSinglePenStyle(code)?.fillColor || "#94a3b8",
    readback,
    autoFlaggedBy: finding.auto_flagged_by || null,
  };
}

function getGroupId(categoryCode, code, isSinglePen) {
  if (isSinglePen) return "flags";
  return GROUP_BY_CODE[categoryCode] || GROUP_BY_CODE[code] || "other";
}

function normalizeSurfaces(surfaces = []) {
  const set = new Set((surfaces || []).filter(Boolean));
  return SURFACE_ORDER.filter((surface) => set.has(surface));
}

function extractSeverityLabel(catalog, categoryCode, code, isSinglePen) {
  if (isSinglePen) return null;
  if (catalog.readback_label) return catalog.readback_label;
  if (catalog.readbackLabel) return catalog.readbackLabel;
  if (catalog.severity_rank || catalog.is_palette_severity || catalog.category_code) {
    const display = catalog.display_name || catalog.name || "";
    const [, afterDash] = display.split(" - ");
    if (afterDash) return translateSeverity(categoryCode, afterDash, code);
    if (code !== categoryCode) return translateSeverity(categoryCode, display, code);
  }
  if (SEVERITY_CATEGORY_CODES.has(categoryCode) || SEVERITY_CATEGORY_CODES.has(code)) return "Unspecified";
  return catalog.has_severity ? "Unspecified severity" : null;
}

export function formatFindingReadback(finding) {
  const parts = [`Tooth ${finding.toothNumber}`];
  if (finding.surfaces?.length) parts.push(finding.surfaces.join(""));
  if (finding.severityLabel) parts.push(finding.severityLabel);
  else if (finding.isSinglePen) parts.push(finding.singlePenLabel);
  return [parts.join(" · "), finding.suffix].filter(Boolean).join(" ");
}

export function translateSeverity(categoryCode, label, code) {
  const raw = String(label || "").trim();
  const lower = raw.toLowerCase();
  if (categoryCode === "defective_restoration" && lower === "open") return "Open margin";
  if (categoryCode === "defective_restoration" && lower === "lost") return "Filling lost";
  if (categoryCode === "fracture" && lower === "dent") return "Into dentin";
  if (categoryCode === "fracture" && lower === "enam") return "Into enamel";
  if (categoryCode === "wear" && lower === "dent") return "Into dentin";
  if (categoryCode === "wear" && lower === "enam") return "Into enamel";
  const token = lower || String(code || "").split("_").pop();
  return SEVERITY_READBACK[token] || raw || null;
}

function groupEntries(entries) {
  const grouped = {};
  for (const entry of entries) {
    if (!grouped[entry.groupId]) grouped[entry.groupId] = [];
    grouped[entry.groupId].push(entry);
  }
  return grouped;
}

function buildCollapsedSummary(grouped, total) {
  if (total === 0) return "No findings charted yet.";
  const parts = GROUPS
    .map((group) => {
      const count = grouped[group.id]?.length || 0;
      if (!count) return null;
      const label = count === 1 ? group.collapsedLabel : pluralize(group.collapsedLabel);
      return `${count} ${label}`;
    })
    .filter(Boolean);
  return `${total} total — ${parts.join(", ")}`;
}

function pluralize(label) {
  if (label === "caries") return "caries";
  if (label === "perio") return "perio";
  if (label === "endo") return "endo";
  return `${label}s`;
}
